<template>
    <div>
        <!-- Action Buttons -->
        <div class="receipt-actions">
            <button @click="printReceipt" class="btn-print">
                <span>🖨️</span> Print Receipt
            </button>
            <button @click="downloadAsImage" class="btn-download" :disabled="downloading">
                <span>📷</span> {{ downloading ? 'Generating...' : 'Download as Image' }}
            </button>
        </div>

        <!-- Receipt Container - Thermal Style -->
        <div ref="receiptContent" class="thermal-receipt">
            <div class="receipt-paper">
                <!-- Store Header -->
                <div class="store-header">
                    <h1 class="store-name">{{ storeInfo.name }}</h1>
                    <p class="store-address">{{ storeInfo.address }}</p>
                    <p class="store-contact">{{ storeInfo.phone }}</p>
                    <p class="store-contact">{{ storeInfo.email }}</p>
                    <!-- <p class="store-tax">TIN: {{ storeInfo.taxId }}</p> -->
                </div>

                <div class="receipt-divider">⚡⚡⚡⚡⚡⚡⚡⚡⚡⚡⚡⚡⚡⚡⚡</div>

                <!-- Receipt Title -->
                <div class="receipt-title">
                    <h2>ACKNOWLEDGMENT RECEIPT</h2>
                    <p class="receipt-no">Receipt #: {{ receipt.syspk_te?.toString().padStart(8, '0') }}</p>
                </div>

                <!-- Date & Time -->
                <div class="datetime">
                    <p>Date: {{ formatDate(receipt.dateadded_te) }}</p>
                    <p>Time: {{ formatTime(receipt.dateadded_te) }}</p>
                </div>

                <!-- Client Info -->
                <div class="client-info">
                    <p><span class="label">Received from:</span> {{ receipt.fullname_client }}</p>
                    <p><span class="label">Account No.:</span> {{ receipt.account_number }}</p>
                    <!-- <p><span class="label">Address:</span> {{ receipt.client_address }}</p> -->
                </div>

                <div class="receipt-divider">═══════════════════════════</div>

                <!-- Payment Details -->
                <div class="payment-details">
                    <p class="payment-label">PAYMENT DETAILS</p>

                    <div class="detail-row">
                        <span>Payment for:</span>
                        <span>{{ receipt.payment_period }}</span>
                    </div>

                    <div class="detail-row">
                        <span>Mode of Payment:</span>
                        <span class="payment-mode">{{ receipt.payment_mode }}</span>
                    </div>

                    <div class="detail-row">
                        <span>Reference No.:</span>
                        <span>{{ receipt.syspk_te?.toString().padStart(8, '0') || 'N/A' }}</span>
                    </div>

                    <!-- <div class="detail-row">
                        <span>OR Number:</span>
                        <span>{{ receipt.or_number }}</span>
                    </div> -->

                    <div class="amount-row">
                        <span class="amount-label">AMOUNT PAID:</span>
                        <span class="amount-value">{{ formatCurrency(receipt.amount_te) }}</span>
                    </div>

                    <div class="detail-row" v-if="receipt.balance">
                        <span>Remaining Balance:</span>
                        <span>{{ formatCurrency(receipt.balance) }}</span>
                    </div>
                </div>

                <div class="receipt-divider">═══════════════════════════</div>


                <!-- Footer -->
                <div class="receipt-footer">
                    <p class="received-by">Received by: {{ receipt.received_by }}</p>
                    <p class="customer-signature">Customer Signature: ___________________</p>
                    <p class="thank-you">THANK YOU FOR YOUR PAYMENT!</p>
                    <!-- <p class="note">{{ receipt.note || 'This serves as your official receipt' }}</p> -->
                    <p class="timestamp">Printed: {{ printTimestamp }}</p>
                </div>

                <!-- Thermal Paper Cut Line -->
                <div class="cut-line">✂️ - - - - - - - - - - - - - - - - ✂️</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'
import Formatters from '@/utils/formatters'
//eslint-disable-next-line
const props = defineProps({
    receipt: {
        type: Object,
        required: true,
        default: () => ({
            receipt_number: 'RCT-2024-001',
            payment_date: new Date().toISOString(),
            client_name: 'John Doe',
            account_number: 'ACC-001',
            client_address: '123 Main St, City',
            payment_period: 'March 2024',
            payment_mode: 'Cash',
            reference_no: 'REF123456',
            or_number: 'OR-2024-001',
            amount_paid: 1250.75,
            balance: 250.25,
            received_by: 'Cashier Name'
        })
    },
    storeInfo: {
        type: Object,
        default: () => ({
            name: 'EXODOUS I.T. SOLUTIONS',
            address: 'Sitio Bakilid, San Juan, Palompon, Leyte',
            phone: '09703287224',
            email: 'exodoustechsolution@gmail.com',
            taxId: ''
        })
    },
    verificationUrl: {
        type: String,
        default: 'https://yourdomain.com/verify/'
    }
})
//eslint-disable-next-line
const emit = defineEmits(['printed', 'downloaded'])

const receiptContent = ref(null)
const qrCodeRef = ref(null)
const downloading = ref(false)
const printTimestamp = ref('')

// Generate QR Code
const generateQRCode = async () => {
    await nextTick()
    if (!qrCodeRef.value) return

    const verificationData = {
        receipt: props.receipt.syspk_te?.toString().padStart(8, '0'),
        amount: props.receipt.amount_te,
        date: props.receipt.dateissued_te,
        client: props.receipt.account_number
    }

    const qrData = `${props.verificationUrl}${btoa(JSON.stringify(verificationData))}`
    try {
        await QRCode.toCanvas(qrCodeRef.value, qrData, {
            width: 120,
            margin: 1,
            color: {
                dark: '#000000',
                light: '#ffffff'
            }
        })
        console.error("QR error")
    } catch (error) {
        console.error('QR Code generation failed:', error)
    }
}

// Format helpers
const formatCurrency = (amount) => {
    return Formatters.formatCurrency(amount)
}

const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}



// Print receipt
const printReceipt = () => {
    updateTimestamp()

    nextTick(() => {
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
      <html>
        <head>
          <title>Receipt - ${props.receipt.receipt_number}</title>
          <style>
            @page {
              size: 80mm auto;
              margin: 0;
            }
            body {
              font-family: 'Courier New', monospace;
              width: 72mm;
              margin: 0 auto;
              padding: 5mm;
              background: white;
              color: black;
              font-size: 12px;
              line-height: 1.3;
            }
            .receipt-paper {
              width: 100%;
            }
            .store-header {
              text-align: center;
              margin-bottom: 5px;
            }
            .store-name {
              font-size: 18px;
              font-weight: bold;
              margin: 0 0 5px 0;
              text-transform: uppercase;
            }
            .store-address, .store-contact, .store-tax {
              font-size: 10px;
              margin: 2px 0;
            }
            .receipt-divider {
              text-align: center;
              font-size: 12px;
              margin: 8px 0;
              letter-spacing: 2px;
            }
            .receipt-title {
              text-align: center;
              margin: 10px 0;
            }
            .receipt-title h2 {
              font-size: 16px;
              margin: 0 0 5px 0;
              letter-spacing: 1px;
            }
            .receipt-no {
              font-size: 11px;
              color: #333;
            }
            .datetime {
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              margin: 8px 0;
            }
            .client-info {
              margin: 10px 0;
              font-size: 11px;
            }
            .client-info p {
              margin: 3px 0;
            }
            .label {
              font-weight: bold;
            }
            .payment-details {
              margin: 10px 0;
            }
            .payment-label {
              font-weight: bold;
              text-align: center;
              margin: 5px 0;
              font-size: 12px;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              margin: 3px 0;
            }
            .amount-row {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              font-size: 14px;
              margin: 10px 0;
              padding: 5px 0;
              border-top: 1px dashed #000;
              border-bottom: 1px dashed #000;
            }
            .payment-mode {
              text-transform: uppercase;
            }
            .amount-words {
              margin: 10px 0;
              font-size: 10px;
              font-style: italic;
            }
            .words {
              margin: 3px 0 0 0;
              text-transform: capitalize;
            }
            .qr-section {
              text-align: center;
              margin: 15px 0;
            }
            .qr-code {
              display: inline-block;
              padding: 5px;
              background: white;
            }
            .qr-label {
              font-size: 9px;
              margin: 3px 0 0 0;
              color: #666;
            }
            .receipt-footer {
              margin: 15px 0 5px 0;
              font-size: 10px;
            }
            .received-by {
              margin: 5px 0;
            }
            .customer-signature {
              margin: 10px 0;
            }
            .thank-you {
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              margin: 10px 0;
              letter-spacing: 1px;
            }
            .note {
              text-align: center;
              font-size: 9px;
              color: #666;
              margin: 5px 0;
            }
            .timestamp {
              text-align: center;
              font-size: 8px;
              color: #999;
              margin: 5px 0;
            }
            .cut-line {
              text-align: center;
              font-size: 10px;
              margin: 10px 0 0 0;
              color: #666;
            }
            @media print {
              body { margin: 0; padding: 2mm; }
            }
          </style>
        </head>
        <body>
          ${receiptContent.value.outerHTML}
        </body>
      </html>
    `)

        printWindow.document.close()
        printWindow.focus()

        printWindow.onafterprint = () => {
            printWindow.close()
            emit('printed', { success: true })
        }

        setTimeout(() => {
            printWindow.print()
        }, 250)
    })
}

// Download as image
const downloadAsImage = async () => {
    downloading.value = true
    updateTimestamp()

    try {
        await nextTick()

        const canvas = await html2canvas(receiptContent.value, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            allowTaint: true,
            useCORS: true
        })

        // Create download link
        const link = document.createElement('a')
        link.download = `receipt_${props.receipt.syspk_te}_${Date.now()}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()

        emit('downloaded', { success: true })
    } catch (error) {
        console.error('Failed to download receipt:', error)
        alert('Failed to download receipt. Please try again.')
    } finally {
        downloading.value = false
    }
}

// Update timestamp
const updateTimestamp = () => {
    const now = new Date()
    printTimestamp.value = now.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

watch(() => props.receipt, (val) => {
    val;
    generateQRCode();
}, { deep: true, immediate: true })

// Lifecycle
onMounted(async () => {
    updateTimestamp()
    await generateQRCode()
})
</script>

<style scoped>
.receipt-actions {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
}

.btn-print,
.btn-download {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}

.btn-print {
    background: #3b82f6;
    color: white;
}

.btn-print:hover {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-download {
    background: #10b981;
    color: white;
}

.btn-download:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-download:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Thermal Receipt Style */
.thermal-receipt {
    max-width: 80mm;
    /* Standard thermal paper width */
    margin: 0 auto;
    background: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.receipt-paper {
    padding: 5mm;
    font-family: 'Courier New', monospace;
    background: white;
    color: black;
    font-size: 12px;
    line-height: 1.3;
}

.store-header {
    text-align: center;
    margin-bottom: 5px;
}

.store-name {
    font-size: 18px;
    font-weight: bold;
    margin: 0 0 5px 0;
    text-transform: uppercase;
    color: #000;
}

.store-address,
.store-contact,
.store-tax {
    font-size: 10px;
    margin: 2px 0;
    color: #333;
}

.receipt-divider {
    text-align: center;
    font-size: 12px;
    margin: 8px 0;
    letter-spacing: 2px;
    color: #000;
}

.receipt-title {
    text-align: center;
    margin: 10px 0;
}

.receipt-title h2 {
    font-size: 16px;
    margin: 0 0 5px 0;
    letter-spacing: 1px;
    font-weight: bold;
}

.receipt-no {
    font-size: 11px;
    color: #333;
}

.datetime {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    margin: 8px 0;
}

.client-info {
    margin: 10px 0;
    font-size: 11px;
}

.client-info p {
    margin: 3px 0;
}

.label {
    font-weight: bold;
}

.payment-details {
    margin: 10px 0;
}

.payment-label {
    font-weight: bold;
    text-align: center;
    margin: 5px 0;
    font-size: 12px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    margin: 3px 0;
}

.amount-row {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    font-size: 14px;
    margin: 10px 0;
    padding: 5px 0;
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
}

.payment-mode {
    text-transform: uppercase;
}

.amount-words {
    margin: 10px 0;
    font-size: 10px;
    font-style: italic;
}

.words {
    margin: 3px 0 0 0;
    text-transform: capitalize;
}

.qr-section {
    text-align: center;
    margin: 15px 0;
}

.qr-code {
    display: inline-block;
    padding: 5px;
    background: white;
}

.qr-label {
    font-size: 9px;
    margin: 3px 0 0 0;
    color: #666;
}

.receipt-footer {
    margin: 15px 0 5px 0;
    font-size: 10px;
}

.received-by {
    margin: 5px 0;
}

.customer-signature {
    margin: 10px 0;
}

.thank-you {
    text-align: center;
    font-weight: bold;
    font-size: 12px;
    margin: 10px 0;
    letter-spacing: 1px;
}

.note {
    text-align: center;
    font-size: 9px;
    color: #666;
    margin: 5px 0;
}

.timestamp {
    text-align: center;
    font-size: 8px;
    color: #999;
    margin: 5px 0;
}

.cut-line {
    text-align: center;
    font-size: 10px;
    margin: 10px 0 0 0;
    color: #666;
}

/* Responsive for mobile */
@media (max-width: 80mm) {
    .thermal-receipt {
        max-width: 100%;
        border-radius: 0;
    }
}
</style>